import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';

const steps = [
  {
    id: '1',
    title: 'Secure your desired plot and location',
    description:
      'Choose the exact area and plot size that fits your lifestyle. We guide you on utilities, access roads, and land verification.',
    image:
      'https://ik.imagekit.io/hqhiltiie/Nyinimu/7007.jpg?updatedAt=1763050111781',
  },
  {
    id: '2',
    title: 'Choose your house design',
    description:
      'Pick from studio, 1BR, 2BR, or 3BR designs—or tailor a layout to your budget, space, and timeline.',
    image:
      'https://ik.imagekit.io/hqhiltiie/Nyinimu/Houses/cb2baf96206eb2102bed98f0260c61b8ab.jpg?updatedAt=1763144216693',
  },
  {
    id: '3',
    title: 'Choose your payment plan',
    description:
      'Select a daily, weekly, or monthly plan that matches your income flow. Get a clear schedule of milestones and costs.',
    image:
      'https://ik.imagekit.io/hqhiltiie/Nyinimu/wmremove-transformed%20(1).jpeg?updatedAt=1763149995209',
  },
  {
    id: '4',
    title: 'Meet the requirements to qualify for the plan',
    description:
      'Provide basic KYC, proof of income, and plot documentation. Our team supports you through any missing paperwork.',
    image:
      'https://ik.imagekit.io/hqhiltiie/Nyinimu/wmremove-transformed.jpeg',
  },
];

const HowItWorksSplit = () => {
  const [active, setActive] = useState('1');
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const activeStep = useMemo(() => steps.find((s) => s.id === active) || steps[0], [active]);

  useEffect(() => {
    if (paused) return;
    timerRef.current && clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((prev) => {
        const idx = steps.findIndex((s) => s.id === prev);
        const next = (idx + 1) % steps.length;
        return steps[next].id;
      });
    }, 4000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [paused]);

  return (
    <section
      className="relative overflow-hidden bg-[#0e1b28] py-24"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Decorative accents in brand green */}
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rotate-12 rounded-3xl bg-[#81be00] blur-3xl opacity-20" />
      <div className="pointer-events-none absolute -right-24 -bottom-16 h-80 w-80 -rotate-12 rounded-3xl bg-[#81be00] blur-3xl opacity-20" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        {/* Left: wide house image (not a phone) */}
        <div className="relative mx-auto w-full max-w-2xl">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="relative"
          >
            {/* subtle backdrop card */}
            <div className="absolute -left-8 top-8 hidden h-72 w-72 -rotate-3 rounded-2xl bg-white/5 shadow-2xl ring-1 ring-white/10 backdrop-blur-sm md:block" />

            {/* foreground wide image */}
            <div className="relative h-[420px] w-full md:h-[540px] overflow-hidden rounded-2xl border border-white/10 bg-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.55)] ring-1 ring-white/20">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeStep.id}
                  src={activeStep.image}
                  alt={activeStep.title}
                  className="h-full w-full object-cover"
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0.2, scale: 0.98 }}
                  transition={{ duration: 0.4 }}
                />
              </AnimatePresence>
              <div className="pointer-events-none absolute -left-8 top-6 h-16 w-16 rotate-12 rounded-2xl bg-[#81be00] opacity-70 blur-md" />
              <div className="pointer-events-none absolute -right-8 bottom-6 h-16 w-16 -rotate-12 rounded-2xl bg-[#81be00] opacity-70 blur-md" />
            </div>
          </motion.div>
        </div>

        {/* Right: content + vertical accordion stepper */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-3 text-xs font-medium tracking-[0.18em] text-[#81be00]"
          >
            STEPS
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="title-font text-4xl font-light leading-tight text-white md:text-5xl"
          >
            <span className="bg-gradient-to-br from-[#81be00] to-[#a6d94a] bg-clip-text text-transparent">Steps to</span>{' '}
            <span className="italic text-white/90">Home Ownership</span>
          </motion.h2>

          <div className="mt-10">
            <Accordion type="single" collapsible value={active} onValueChange={(v) => v && setActive(v)}>
              {steps.map((s, i) => (
                <AccordionItem key={s.id} value={s.id} className="border-b border-white/10">
                  <AccordionTrigger className="py-6 hover:no-underline">
                    <div className="relative pl-16 w-full text-left">
                      {/* connector line */}
                      {i < steps.length - 1 && (
                        <span className="absolute left-6 top-8 hidden h-12 w-px bg-white/20 md:block" />
                      )}
                      {/* number circle */}
                      <span
                        className={[
                          'absolute left-0 top-0 grid h-12 w-12 place-items-center rounded-full border text-sm font-semibold transition-colors',
                          active === s.id
                            ? 'border-transparent bg-[#81be00] text-[#0e1b28] shadow-[0_0_0_6px_rgba(129,190,0,0.2)]'
                            : 'border-white/30 text-white/80',
                        ].join(' ')}
                      >
                        {s.id}
                      </span>
                      <h3 className="text-xl md:text-2xl font-medium text-[#81be00]">{s.title}</h3>
                      <p className="mt-1 text-sm md:text-base text-white/60">{s.description}</p>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-16 pr-2 pb-6 text-white/70">
                      {/* Optional extra detail per step */}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSplit;
